import React from 'react';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import { List, ListItem, ListItemText, Divider, Typography } from '@material-ui/core';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useGetStockMovementsQuery } from 'generated/graphql';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import { format } from 'date-fns';
import CachedIcon from '@material-ui/icons/Cached';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

interface StockMovementDialogProps {
	open: boolean;
	onClose: () => void;
	menuItemID: string | null;
	outletID: string;
	startDate: Date | null;
	endDate: Date | null;
}

export const StockMovementDialog: React.FC<StockMovementDialogProps> = ({
	open,
	onClose,
	menuItemID,
	outletID,
	startDate,
	endDate,
}) => {
	const { data, loading, error } = useGetStockMovementsQuery({
		variables: {
			outletID: outletID || '',
			menuItemID: menuItemID || '',
			startDate: startDate || undefined,
			endDate: endDate || undefined,
		},
		skip: !menuItemID || !outletID || !open,
		fetchPolicy: 'network-only',
	});

	const movements = data?.getStockMovements || [];

	const getMovementTypeLabel = (type: string, isOpening?: boolean) => {
		if (type === 'STOCK_IN' && isOpening) {
			return 'Opening Balance';
		}
		switch (type) {
			case 'STOCK_IN':
				return 'Stock In';
			case 'STOCK_OUT':
				return 'Stock Out';
			case 'STOCK_ADJUSTMENT':
				return 'Stock Adjustment';
			case 'SOLD_OUT':
				return 'Sold Out';
			default:
				return type;
		}
	};

	const getMovementTypeIcon = (type: string, isOpening?: boolean) => {
		if (type === 'STOCK_IN' && isOpening) {
			return <AccountBalanceIcon style={{ fontSize: '16px', color: '#2196f3', marginRight: '4px' }} />;
		}
		switch (type) {
			case 'STOCK_IN':
				return <TrendingDownIcon style={{ fontSize: '16px', color:  '#4caf50' , marginRight: '4px' }} />;
				
			case 'STOCK_OUT':
				return <TrendingUpIcon style={{ fontSize: '16px', color: '#f44336', marginRight: '4px' }} />;
			case 'STOCK_ADJUSTMENT':
				return <CachedIcon style={{ fontSize: '16px', color: '#9e9e9e', marginRight: '4px' }} />;
			case 'SOLD_OUT':
				return <ShoppingCartIcon style={{ fontSize: '16px', color: '#ff9800', marginRight: '4px' }} />;
			default:
				return null;
		}
	};

	const getQuantityLabel = (type: string) => {
		switch (type) {
			case 'STOCK_IN':
				return 'Qty Received';
			case 'STOCK_OUT':
				return 'Transfer Qty';
			case 'STOCK_ADJUSTMENT':
				return 'Qty Adjusted';
			case 'SOLD_OUT':
				return 'Qty Sold';
			default:
				return 'Quantity';
		}
	};

	const getQuantityValue = (movement: any) => {
		switch (movement.movementType) {
			case 'STOCK_IN':
				return movement.qtyReceived;
			case 'STOCK_OUT':
				return movement.transferQty;
			case 'STOCK_ADJUSTMENT':
				return movement.qtyAdjusted;
			case 'SOLD_OUT':
				return movement.qtySold;
			default:
				return null;
		}
	};

	return (
		<CommonDialog
			fullWidth
			maxWidth="md"
			open={open}
			onClose={onClose}
			sections={{
				header: {
					dynamic: (
						<div className="dialog-dynamic-content">
							<div className="title">Item Movements</div>
						</div>
					),
				},
				body: () => (
					<div style={{ minHeight: '200px' }}>
						{loading ? (
							<Loading />
						) : error ? (
							<Typography variant="body2" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
								Error loading movements: {error.message}
							</Typography>
						) : movements.length === 0 ? (
							<EmptyList
								title="No movements found"
								subtitle="Item movements will appear here"
							/>
						) : (
							<List className="core-list">
								{movements.map((movement: any, index: number) => {
									const quantityValue = getQuantityValue(movement);
									const quantityDisplay = quantityValue !== null && quantityValue !== undefined
										? parseFloat(String(quantityValue)).toFixed(4)
										: '-';

									return (
										<React.Fragment key={movement.ID}>
											<ListItem>
												<ListItemText
													primary={
														<span className="flex-space desc" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
															<span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
																{getMovementTypeIcon(movement.movementType, movement.isOpening)}
																{getMovementTypeLabel(movement.movementType, movement.isOpening)}
															</span>
															<span className="desc right-info" style={{ flexShrink: 0, marginLeft: '8px' }}>
																{quantityDisplay} {movement.uomName ? `/${movement.uomName}` : ''}
															</span>
														</span>
													}
													secondary={
														<>
															<div className="desc">
																{format(new Date(movement.trxDate), 'dd/MM/yyyy')}
																{movement.docNo && ` | ${movement.docNo}`}
																{movement.docRef && ` | ${movement.docRef}`}
															</div>
															{movement.description && (
																<div className="desc" style={{ marginLeft: '4px' }}>
																	{movement.description}
																</div>
															)}
															{movement.movementType === 'STOCK_OUT' || movement.movementType === 'STOCK_ADJUSTMENT' ? (
																movement.reasonName && (
																	<div className="desc" style={{ marginLeft: '4px' }}>
																		Reason: {movement.reasonName}
																	</div>
																)
															) : null}
															{movement.remark && (
																<div className="desc" style={{ marginLeft: '4px', fontStyle: 'italic' }}>
																	{movement.remark}
																</div>
															)}
														</>
													}
												/>
											</ListItem>
											{index < movements.length - 1 && <Divider component="li" />}
										</React.Fragment>
									);
								})}
							</List>
						)}
					</div>
				),
				footer: {
					actions: [
						{
							displayText: 'Close',
							props: {
								onClick: onClose,
								variant: 'contained',
								color: 'primary',
							},
						},
					],
				},
			}}
		/>
	);
};

